import React, { useState, useRef, useEffect } from "react";
import { Add as Plus, Check, Close as X, Edit as Pencil, InfoOutlined, SwapHoriz as MoveIcon, DeleteOutline as TrashIcon } from "@mui/icons-material";
import { Box, Tabs, Tab, IconButton, TextField, Button, Popover, Typography, Divider, List, ListItem, ListItemText, Menu, MenuItem } from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableTab = React.forwardRef((props, ref) => {
  const { id, style: externalStyle, ...otherProps } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    ...externalStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 1,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Tab
      ref={(node) => {
        setNodeRef(node);
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      style={style}
      {...attributes}
      {...listeners}
      {...otherProps}
    />
  );
});

export const TabBar = ({
  tabs,
  activeTabId,
  setActiveTabId,
  onAddTab,
  onRenameTab,
  onUpdateTab,
  onMoveTab,
  onReorderTab,
  moveOptions,
  extraActions
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newTabName, setNewTabName] = useState("");
  const [editingTabId, setEditingTabId] = useState(null);
  const [editTabName, setEditTabName] = useState("");
  const inputRef = useRef(null);
  const tabEditInputRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTabInfo, setSelectedTabInfo] = useState(null);

  const [moveAnchorEl, setMoveAnchorEl] = useState(null);
  const [tabToMove, setTabToMove] = useState(null);

  const [newPropKey, setNewPropKey] = useState("");
  const [newPropVal, setNewPropVal] = useState("");

  const handleMoveClick = (event, tab) => {
    event.stopPropagation();
    setMoveAnchorEl(event.currentTarget);
    setTabToMove(tab);
  };

  const handleMoveClose = (event) => {
    if (event) event.stopPropagation();
    setMoveAnchorEl(null);
    setTabToMove(null);
  };

  const handleMoveSelect = (option) => {
    if (onMoveTab && tabToMove) {
      onMoveTab(tabToMove.id || tabToMove.ScreenName, option.category, option.subCategory);
    }
    handleMoveClose();
  };

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  useEffect(() => {
    if (editingTabId && tabEditInputRef.current) {
      tabEditInputRef.current.focus();
      tabEditInputRef.current.select();
    }
  }, [editingTabId]);

  const handleStartAdd = () => {
    setIsAdding(true);
    setNewTabName("");
  };

  const handleSave = () => {
    const trimmedName = newTabName.trim();
    if (trimmedName) {
      onAddTab(trimmedName);
      setIsAdding(false);
    } else {
      handleCancel();
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setNewTabName("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleStartRename = (e, tabId, currentName) => {
    e.stopPropagation();
    setEditingTabId(tabId);
    setEditTabName(currentName);
  };

  const handleSaveRename = (tabId) => {
    const trimmed = editTabName.trim();
    if (trimmed) {
      onRenameTab(tabId, trimmed);
    }
    setEditingTabId(null);
  };

  const handleRenameKeyDown = (e, tabId) => {
    if (e.key === "Enter") {
      handleSaveRename(tabId);
    } else if (e.key === "Escape") {
      setEditingTabId(null);
    }
  };

  const handleChange = (event, newValue) => {
    if (newValue !== "adding-new-tab") {
      setActiveTabId(newValue);
    }
  };

  const handlePopoverOpen = (event, tab) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTabInfo(tab);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedTabInfo(null);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      if (onReorderTab) {
        onReorderTab(active.id, over.id);
      }
    }
  };

  const handleTabInfoChange = (key, value) => {
    setSelectedTabInfo(prev => ({ ...prev, [key]: value }));
  };

  const handleDeleteTabInfoProperty = (key) => {
    setSelectedTabInfo(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const handleAddProperty = () => {
    if (newPropKey.trim()) {
      setSelectedTabInfo(prev => ({ ...prev, [newPropKey.trim()]: newPropVal }));
      setNewPropKey("");
      setNewPropVal("");
    }
  };

  const handleSaveTabInfo = () => {
    if (onUpdateTab && selectedTabInfo) {
      onUpdateTab(selectedTabInfo.id || selectedTabInfo.ScreenName, selectedTabInfo);
    }
    handlePopoverClose();
  };

  const openPopover = Boolean(anchorEl);

  return (
    <Box className="schema-tabs-container" sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', mb: 3, gap: 2 }}>
      <DndContext 
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex' }}>
          <SortableContext items={tabs.map(t => t.id || t.ScreenName)} strategy={horizontalListSortingStrategy}>
            <Tabs
              value={activeTabId || false}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ flexGrow: 1, minWidth: 0 }}
            >
          {tabs.map((tab) => {
            const isEditingThisTab = editingTabId === tab.id;

            const labelNode = isEditingThisTab ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                <TextField
                  inputRef={tabEditInputRef}
                  size="small"
                  variant="standard"
                  value={editTabName}
                  onChange={(e) => setEditTabName(e.target.value)}
                  onKeyDown={(e) => handleRenameKeyDown(e, tab.id)}
                  inputProps={{ maxLength: 25 }}
                />
                <IconButton size="small" onClick={() => handleSaveRename(tab.id)} color="primary">
                  <Check fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setEditingTabId(null)} color="error">
                  <X fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span style={{ marginRight: '4px' }}>{tab.name || tab.ScreenName}</span>
                <IconButton
                  size="small"
                  onClick={(e) => handlePopoverOpen(e, tab)}
                  sx={{ opacity: 0.6, '&:hover': { opacity: 1 }, padding: '2px' }}
                >
                  <InfoOutlined fontSize="small" />
                </IconButton>
                {moveOptions && moveOptions.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleMoveClick(e, tab)}
                    sx={{ opacity: 0.6, '&:hover': { opacity: 1 }, padding: '2px' }}
                    title="Move Tab"
                  >
                    <MoveIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            );

            return (
              <SortableTab
                key={tab.id || tab.ScreenName}
                id={tab.id || tab.ScreenName}
                value={tab.id || tab.ScreenName}
                label={labelNode}
                sx={{ textTransform: 'none', minHeight: 48 }}
              />
            );
          })}

          {isAdding && (
            <Tab
              value="adding-new-tab"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    inputRef={inputRef}
                    size="small"
                    variant="standard"
                    value={newTabName}
                    onChange={(e) => setNewTabName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="New Tab"
                    inputProps={{ maxLength: 20 }}
                  />
                  <IconButton size="small" onClick={handleSave} color="primary">
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={handleCancel} color="error">
                    <X fontSize="small" />
                  </IconButton>
                </Box>
              }
            />
            )}
            </Tabs>
          </SortableContext>
        </Box>
      </DndContext>
      <Box sx={{ display: 'flex', flexDirection: "row", gap: 2, flexWrap: 'nowrap', ml: 'auto', alignItems: 'center', flexShrink: 0 }}>
        <Button
          variant="outlined"
          startIcon={<Plus />}
          onClick={handleStartAdd}
          disabled={isAdding}
          size="small"
          sx={{ whiteSpace: 'nowrap', py: 0.25, px: 1.5, fontSize: '0.75rem', height: 32, borderRadius: 2 }}
        >
          Add Tab
        </Button>
        {extraActions}
      </Box>

      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: { mt: 1, p: 2, minWidth: 320, maxWidth: 400, borderRadius: 2, boxShadow: 3 }
        }}
      >
        {selectedTabInfo && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Edit Tab Details
            </Typography>
            <Divider />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 400, overflowY: 'auto', py: 1, px: 0.5 }}>
              {Object.entries(selectedTabInfo).map(([key, value]) => {
                if (
                  key === 'id' ||
                  key === 'name' ||
                  key === 'SourceFields' ||
                  typeof value === 'function' ||
                  typeof value === 'object'
                ) {
                  return null;
                }
                return (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label={key.replace(/([A-Z])/g, ' $1').trim()}
                      size="small"
                      value={value || ''}
                      onChange={(e) => handleTabInfoChange(key, e.target.value)}
                      fullWidth
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                    <IconButton size="small" onClick={() => handleDeleteTabInfoProperty(key)} color="error" title="Delete Property">
                      <TrashIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                label="New Key"
                value={newPropKey}
                onChange={(e) => setNewPropKey(e.target.value)}
                sx={{ width: '40%' }}
              />
              <TextField
                size="small"
                label="Value"
                value={newPropVal}
                onChange={(e) => setNewPropVal(e.target.value)}
                sx={{ flexGrow: 1 }}
              />
              <IconButton size="small" onClick={handleAddProperty} color="primary" disabled={!newPropKey.trim()} title="Add Property">
                <Plus fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button size="small" onClick={handlePopoverClose} color="inherit">
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={handleSaveTabInfo}>
                Save
              </Button>
            </Box>
          </Box>
        )}
      </Popover>

      <Menu
        anchorEl={moveAnchorEl}
        open={Boolean(moveAnchorEl)}
        onClose={handleMoveClose}
      >
        {moveOptions && moveOptions.map((opt, idx) => (
          <MenuItem key={idx} onClick={() => handleMoveSelect(opt)}>
            Move to {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default TabBar;
